/** The five layout shells. */

import { Suspense } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { ROUTES } from '@metal/shared'
import {
  AppBar,
  AppShell,
  Brand,
  Button,
  GradeChip,
  Icon,
  IconButton,
  MarketTicker,
  Nav,
  SessionClock,
  Skeleton,
  Topbar,
  type NavItemSpec,
} from '@metal/ui'
import { useMarketClock, useSession } from '@metal/ui/session'
import { usePublicTicker, useTicker } from '@/lib/queries'
import { AccountMenu } from './AccountMenu'
import { useAuthModal } from './AppRoot'
import { MobileTabBar } from './MobileTabBar'

/** The member rows, built from the account's own trading sides. */
function memberNav(sides: Array<'buy' | 'sell'>): NavItemSpec[] {
  const sells = sides.includes('sell')
  const buys = sides.includes('buy')
  return [
    { to: ROUTES.board(), label: 'تابلو', icon: 'chart', match: ['/board*', '/offers/1*', '/rfqs/2*'] },
    ...(sells
      ? [
          {
            to: ROUTES.myOffers(),
            label: 'عرضه‌های من',
            icon: 'cathode' as const,
            match: ['/offers/new*'],
          },
        ]
      : []),
    ...(buys
      ? [
          {
            to: ROUTES.myRfqs(),
            label: 'تقاضاهای من',
            icon: 'list' as const,
            match: ['/rfqs/new*'],
          },
        ]
      : []),
    { to: ROUTES.bids(), label: 'پیشنهادهای من', icon: 'exchange' },
    { to: ROUTES.deals(), label: 'معاملات', icon: 'doc', match: ['/deals*', '/negotiations*'] },
    { to: ROUTES.marketPrices(), label: 'بازار', icon: 'percent', match: ['/market*'] },
    { to: ROUTES.wallet(), label: 'کیف پول', icon: 'wallet', match: ['/wallet*'] },
  ]
}

const PUBLIC_NAV: NavItemSpec[] = [
  // The board is shown to a visitor but gated:
  { to: ROUTES.board(), label: 'تابلوی معاملات', icon: 'chart', match: ['/board*'] },
  { to: ROUTES.news(), label: 'اخبار', icon: 'doc', match: ['/news*'] },
  { to: ROUTES.ads(), label: 'آگهی‌ها', icon: 'grid' },
  { to: ROUTES.links(), label: 'پیوندها', icon: 'external' },
]

/** The four operator edited pages, in footer order. */
const CONTENT_PAGES: Array<{ slug: string; labelFa: string }> = [
  { slug: 'trading-guide', labelFa: 'راهنمای معامله' },
  { slug: 'terms', labelFa: 'قوانین و مقررات' },
  { slug: 'about', labelFa: 'درباره ما' },
  { slug: 'contact', labelFa: 'تماس با ما' },
]

/** Routes that show the market strip. A wizard does not. */
function Loading() {
  return (
    <div className="page">
      <Skeleton h={28} w="220px" />
      <Skeleton h={180} />
      <Skeleton h={320} />
    </div>
  )
}

/** The visitor's shell. */
export function PublicLayout() {
  const clock = useMarketClock()
  const { user } = useSession()
  const auth = useAuthModal()

  // Signed in, the public bar carries the account itself, not a bare «ورود» to // it:
  const entry = user ? (
    <AccountMenu />
  ) : (
    <Button variant="primary" size="sm" onClick={auth.open}>
      ورود یا ثبت‌نام
    </Button>
  )

  return (
    <AppShell
      topbar={
        <Topbar variant="public">
          <Brand sub="سامانه معاملات مس" />
          <div className="sep" />
          <Nav items={PUBLIC_NAV} />
          <div className="grow" />
          <SessionClock open={clock.open} closesAt={clock.closesAt} opensAt={clock.opensAt} short />
          <div className="sep" />
          {entry}
        </Topbar>
      }
      appbar={
        <AppBar>
          <Brand sub="سامانه معاملات مس" />
          <div className="grow" />
          {entry}
        </AppBar>
      }
      ticker={
        <nav className="subnav" aria-label="ناوبری عمومی">
          {PUBLIC_NAV.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) => (isActive ? 'is-on' : undefined)}
            >
              <Icon name={it.icon} />
              {it.label}
            </NavLink>
          ))}
        </nav>
      }
    >
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
      <PublicFooter />
    </AppShell>
  )
}

/** The footer's one action, drawn as its links are. */
const FOOTER_BUTTON = {
  background: 'none',
  border: 0,
  padding: 0,
  font: 'inherit',
  cursor: 'pointer',
} as const

function PublicFooter() {
  const { user } = useSession()
  const auth = useAuthModal()
  return (
    <footer className="no-print" style={{ borderBlockStart: '1px solid var(--c-border)', marginBlockStart: 'var(--s-9)' }}>
      <div className="page" style={{ paddingBlock: 'var(--s-7)' }}>
        <div className="row-b wrap" style={{ gap: 'var(--s-6)', alignItems: 'flex-start' }}>
          <Brand sub="سامانه معاملات مس" />
          <nav className="row wrap" style={{ gap: 'var(--s-5)' }}>
            <NavLink className="t-sm t-sec" to={ROUTES.news()}>
              اخبار
            </NavLink>
            <NavLink className="t-sm t-sec" to={ROUTES.ads()}>
              آگهی‌ها
            </NavLink>
            <NavLink className="t-sm t-sec" to={ROUTES.links()}>
              پیوندها
            </NavLink>
            {CONTENT_PAGES.map((p) => (
              <NavLink key={p.slug} className="t-sm t-sec" to={ROUTES.contentPage(p.slug)}>
                {p.labelFa}
              </NavLink>
            ))}
            {!user && (
              <button type="button" className="t-sm t-sec" style={FOOTER_BUTTON} onClick={auth.open}>
                ثبت‌نام
              </button>
            )}
          </nav>
        </div>
      </div>
    </footer>
  )
}

/** Sign in and the complete-profile flow. Brand only, no navigation. */
export function AuthLayout() {
  const { user, signOut } = useSession()
  return (
    <div className="app">
      <header className="topbar">
        <Brand sub="سامانه معاملات مس" />
        <div className="grow" />
        {user && (
          <Button variant="ghost" size="sm" icon="logout" onClick={() => void signOut()}>
            خروج
          </Button>
        )}
      </header>
      <main className="main" id="main">
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  )
}

export function MemberLayout({ mode = 'default' }: { mode?: 'default' | 'focus' }) {
  const clock = useMarketClock()
  const { user, loading } = useSession()
  const location = useLocation()
  const ticker = useTicker()

  const showTicker = mode === 'default'

  if (loading) return <Loading />

  // A visitor on a member route:
  if (!user) {
    return (
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
    )
  }

  return (
    <AppShell
      topbar={
        <Topbar variant="app">
          <Brand sub="سامانه معاملات مس" to={ROUTES.dashboard()} />
          <div className="sep" />
          {mode === 'focus' ? (
            <FocusBar />
          ) : (
            <>
              <Nav items={memberNav(user.sides)} />
              <div className="grow" />
              <SessionClock open={clock.open} closesAt={clock.closesAt} opensAt={clock.opensAt} />
              <div className="sep" />
              <div className="row" style={{ gap: 4 }}>
                <IconButton icon="search" label="جست‌وجو" size="sm" />
                <IconButton
                  icon="bell"
                  label="اعلان‌ها"
                  size="sm"
                  pip={user.unreadNotifications > 0}
                  to={ROUTES.notifications()}
                />
                <div className="sep" />
                <AccountMenu />
              </div>
            </>
          )}
        </Topbar>
      }
      appbar={
        <AppBar>
          {mode === 'focus' ? (
            <>
              <span className="t-sm t-sb" style={{ minInlineSize: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {location.pathname.startsWith('/offers') ? 'ثبت عرضه جدید' : 'ثبت تقاضای خرید'}
              </span>
              <div className="grow" />
              <Button
                variant="ghost"
                size="sm"
                icon="x"
                to={location.pathname.startsWith('/offers') ? ROUTES.myOffers() : ROUTES.myRfqs()}
              >
                انصراف
              </Button>
            </>
          ) : (
            <>
              <Brand sub="سامانه معاملات مس" to={ROUTES.dashboard()} />
              <div className="grow" />
              <SessionClock
                open={clock.open}
                closesAt={clock.closesAt}
                opensAt={clock.opensAt}
                short
              />
              <IconButton icon="search" label="جست‌وجو" size="sm" />
              <IconButton
                icon="bell"
                label="اعلان‌ها"
                size="sm"
                pip={user.unreadNotifications > 0}
                to={ROUTES.notifications()}
              />
              <AccountMenu compact />
            </>
          )}
        </AppBar>
      }
      ticker={
        showTicker && ticker.data ? (
          <MarketTicker items={ticker.data.items} />
        ) : undefined
      }
      bottomBar={mode === 'default' ? <MobileTabBar /> : undefined}
    >
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
    </AppShell>
  )
}

/** The wizard bar. */
function FocusBar() {
  const { user } = useSession()
  const location = useLocation()
  const isOffer = location.pathname.startsWith('/offers')
  return (
    <>
      <span className="t-sm t-sb">{isOffer ? 'ثبت عرضه جدید' : 'ثبت تقاضای خرید'}</span>
      <div className="grow" />
      <span className="t-cap t-muted">{user?.shortName}</span>
      <div className="sep" />
      <Button
        variant="ghost"
        size="sm"
        icon="x"
        to={isOffer ? ROUTES.myOffers() : ROUTES.myRfqs()}
      >
        انصراف
      </Button>
    </>
  )
}

/** A page that carries its own chrome. */
export function BareLayout() {
  return (
    <div className="app">
      <main className="main" id="main">
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  )
}

/** The notice and the deal documents print on A4. No application chrome. */
export function PrintLayout() {
  return (
    <div className="app">
      <main className="main" id="main">
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  )
}
